import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { MAX_METRICS_DAYS } from '@/utils/analyticsConstants';

export interface InstagramMetric {
  id: string;
  metric_date: string;
  followers_count: number;
  following_count: number;
  posts_count: number;
  followers_change: number;
  following_change: number;
  posts_change: number;
  followers_growth_rate: number;
  scrape_timestamp: string;
}

export interface AnalyticsData {
  recentMetrics: InstagramMetric | null;
  historicalMetrics: InstagramMetric[];
  totalContentIdeas: number;
  totalInspirationSources: number;
  averageViralScore: number;
  // New detailed content stats
  inspirationIdeasCount: number;
  creatorIdeasCount: number;
  inspirationAvgScore: number;
  creatorAvgScore: number;
}

export const useAnalytics = (userId?: string | null) => {
  const [data, setData] = useState<AnalyticsData>({
    recentMetrics: null,
    historicalMetrics: [],
    totalContentIdeas: 0,
    totalInspirationSources: 0,
    averageViralScore: 0,
    inspirationIdeasCount: 0,
    creatorIdeasCount: 0,
    inspirationAvgScore: 0,
    creatorAvgScore: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!userId) {
        setIsLoading(false);
        return;
      }

      // Get user's Instagram username from user_profiles
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('instagram_username')
        .eq('user_id', userId)
        .single();

      if (profileError) {
        // Treat missing or inaccessible profile as incomplete profile state
        setError('INCOMPLETE_PROFILE');
        setIsLoading(false);
        return;
      }

      if (!userProfile?.instagram_username) {
        // No Instagram profile connected - set special error
        setError('INCOMPLETE_PROFILE');
        setData({
          recentMetrics: null,
          historicalMetrics: [],
          totalContentIdeas: 0,
          totalInspirationSources: 0,
          averageViralScore: 0,
          inspirationIdeasCount: 0,
          creatorIdeasCount: 0,
          inspirationAvgScore: 0,
          creatorAvgScore: 0,
        });
        setIsLoading(false);
        return;
      }

      // Fetch Instagram daily metrics for this username
      const { data: metrics, error: metricsError } = await supabase
        .from('instagram_daily_metrics')
        .select('*')
        .ilike('username', userProfile.instagram_username)
        .order('scraped_at', { ascending: false });

      if (metricsError) {
        throw metricsError;
      }

      // Group by scrape_date and get only the latest entry per day
      const uniqueMetricsByDate = new Map();
      metrics?.forEach(metric => {
        if (!uniqueMetricsByDate.has(metric.scrape_date)) {
          uniqueMetricsByDate.set(metric.scrape_date, metric);
        }
      });

      // Convert to array and limit to most recent days (need 35+ for monthly calculations)
      const uniqueMetrics = Array.from(uniqueMetricsByDate.values())
        .sort((a, b) => new Date(b.scraped_at).getTime() - new Date(a.scraped_at).getTime())
        .slice(0, MAX_METRICS_DAYS);

      // Transform daily metrics to match the expected format
      const transformedMetrics = uniqueMetrics.map(metric => ({
        id: metric.id,
        metric_date: metric.scrape_date,
        followers_count: metric.followers_count,
        following_count: metric.following_count,
        posts_count: metric.posts_count,
        followers_change: 0, // Calculate from previous day if available
        following_change: 0,
        posts_change: 0,
        followers_growth_rate: 0,
        scrape_timestamp: metric.scraped_at,
      }));

      // Calculate changes from previous day
      for (let i = 0; i < transformedMetrics.length - 1; i++) {
        const current = transformedMetrics[i];
        const previous = transformedMetrics[i + 1];
        
        current.followers_change = current.followers_count - previous.followers_count;
        current.following_change = current.following_count - previous.following_count;
        current.posts_change = current.posts_count - previous.posts_count;
        
        // Calculate growth rate
        if (previous.followers_count > 0) {
          current.followers_growth_rate = current.followers_change / previous.followers_count;
        }
      }

      // Fetch content ideas from inspirations
      const { data: contentIdeas, error: contentError } = await supabase
        .from('content_ideas')
        .select('viral_score')
        .eq('user_id', userId)
        .eq('is_trashed', false);

      if (contentError) {
        throw contentError;
      }

      // Fetch user generated content from creator
      const { data: generatedContent, error: generatedError } = await supabase
        .from('user_generated_content')
        .select('id')
        .eq('user_id', userId)
        .eq('is_trashed', false);

      if (generatedError) {
        throw generatedError;
      }

      // Fetch inspiration sources count
      const { data: sources, error: sourcesError } = await supabase
        .from('inspiration_sources')
        .select('id')
        .eq('user_id', userId);

      if (sourcesError) {
        throw sourcesError;
      }

      // Calculate average viral score for content ideas from inspirations
      const validContentScores = (contentIdeas || [])
        .map(idea => idea.viral_score)
        .filter((score): score is number => score !== null && score !== undefined);
      
      const inspirationAvgScore = validContentScores.length > 0 
        ? validContentScores.reduce((sum, score) => sum + score, 0) / validContentScores.length 
        : 0;

      const inspirationIdeasCount = contentIdeas?.length || 0;
      const creatorIdeasCount = generatedContent?.length || 0;
      const totalIdeas = inspirationIdeasCount + creatorIdeasCount;

      setData({
        recentMetrics: transformedMetrics && transformedMetrics.length > 0 ? transformedMetrics[0] : null,
        historicalMetrics: transformedMetrics || [],
        totalContentIdeas: totalIdeas,
        totalInspirationSources: sources?.length || 0,
        averageViralScore: Math.round(inspirationAvgScore * 10) / 10,
        inspirationIdeasCount,
        creatorIdeasCount,
        inspirationAvgScore: Math.round(inspirationAvgScore * 10) / 10,
        creatorAvgScore: 0, // Creator content doesn't have viral scores
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch analytics';
      setError(errorMessage);
      console.error('Error in fetchAnalytics:', err);
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [userId]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchAnalytics
  };
};